import "./lib/readable_stream_polyfill"
import * as pdfjsLib from "pdfjs-dist/build/pdf.mjs"
import { PDFDocument } from "pdf-lib"

// ── Drawing discipline prefixes ──────────────────────────────────────────
const DISCIPLINE_PREFIXES = {
  "FP": "Fire Protection",
  "FA": "Fire Protection",
  "FS": "Fire Protection",
  "SK": "Sketch",
  "SD": "Civil",
  "EC": "Civil",
  "SW": "Civil",
  "UT": "Civil",
  "GR": "Civil",
  "ID": "Architectural",
  "A":  "Architectural",
  "S":  "Structural",
  "T":  "Structural",
  "M":  "Mechanical",
  "E":  "Electrical",
  "P":  "Plumbing",
  "C":  "Civil",
  "L":  "Landscape",
  "G":  "General"
}

const MAX_SPLIT_ROWS = 200

// ── Spec: Extract page headers for LLM analysis ─────────────────────────
// Returns { pages: [{ page: 1, text: "..." }, ...], numPages } or null if too few pages.
// The caller sends `pages` to the server for Gemini analysis.

export async function extractPageHeaders(arrayBuffer, onProgress, minPages = 5) {
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer.slice(0) }).promise
  const numPages = pdf.numPages

  if (numPages < minPages) return null

  onProgress?.({ phase: "scanning", current: 0, total: numPages })

  const pages = []
  for (let i = 1; i <= numPages; i++) {
    const page = await pdf.getPage(i)
    const content = await page.getTextContent()
    const text = content.items.map(item => item.str).join(" ")
    pages.push({ page: i, text })

    if (i % 50 === 0) {
      onProgress?.({ phase: "scanning", current: i, total: numPages })
      await new Promise(r => setTimeout(r, 0))
    }
  }

  onProgress?.({ phase: "scanned", totalPages: numPages })
  return { pages, numPages }
}

// ── Spec: Split PDF by a pre-computed section map ────────────────────────
// sections: array from the LLM: [{ section_number, title, division, start_page }, ...]
// Async generator that yields one split file at a time.

export async function* splitSpecBySections(file, arrayBuffer, sections, numPages, onProgress) {
  if (!sections || sections.length === 0) {
    yield null
    return
  }

  // Sort sections by start_page to be safe
  sections.sort((a, b) => a.start_page - b.start_page)

  // Compute end pages: each section ends where the next one starts — unless
  // the server already knows where it ends (S2-15: a spec section inside an
  // addendum stops at its END OF SECTION line, before the bid form or a sheet).
  for (let i = 0; i < sections.length; i++) {
    sections[i].endPage = sections[i].end_page || ((i + 1 < sections.length) ? sections[i + 1].start_page - 1 : numPages)
  }

  onProgress?.({ phase: "splitting", total: sections.length })

  const srcDoc = await PDFDocument.load(arrayBuffer, { ignoreEncryption: true })
  const basename = file.name.replace(/\.pdf$/i, "")
  let count = 0

  for (const section of sections) {
    if (count >= MAX_SPLIT_ROWS) {
      onProgress?.({ phase: "maxReached", max: MAX_SPLIT_ROWS })
      break
    }

    const newDoc = await PDFDocument.create()
    const pageIndices = []
    for (let p = section.start_page - 1; p <= section.endPage - 1; p++) {
      pageIndices.push(p)
    }
    const copiedPages = await newDoc.copyPages(srcDoc, pageIndices)
    copiedPages.forEach(page => newDoc.addPage(page))

    const pdfBytes = await newDoc.save()
    const blob = new Blob([pdfBytes], { type: "application/pdf" })
    const sectionNum = (section.section_number || "").replace(/\s+/g, "")
    const splitFileName = `${basename}_Section_${sectionNum}.pdf`
    const splitFile = new File([blob], splitFileName, { type: "application/pdf" })

    count++
    yield {
      file: splitFile,
      sectionNumber: section.section_number,
      title: section.title || "",
      division: section.division || "",
      standardDrawing: section.standard_drawing === true,
      startPage: section.start_page,
      endPage: section.endPage,
      pageCount: section.endPage - section.start_page + 1
    }

    onProgress?.({ phase: "splitting", current: count, total: sections.length })
    // Micro-yield so browser paints the row
    await new Promise(r => setTimeout(r, 0))
  }
}

// ── Drawing: Split PDF by LLM-identified sheets ─────────────────────────
// sheets: array from the LLM: [{ sheet_number, title, discipline, page }, ...]
// Each sheet is a single page. Async generator yields one split file at a time.

export async function* splitDrawingBySheets(file, arrayBuffer, sheets, numPages, onProgress) {
  if (!sheets || sheets.length === 0) {
    yield null
    return
  }

  sheets.sort((a, b) => a.page - b.page)

  onProgress?.({ phase: "splitting", total: sheets.length })

  const srcDoc = await PDFDocument.load(arrayBuffer, { ignoreEncryption: true })
  const basename = file.name.replace(/\.pdf$/i, "")
  let count = 0

  for (const sheet of sheets) {
    if (count >= MAX_SPLIT_ROWS) {
      onProgress?.({ phase: "maxReached", max: MAX_SPLIT_ROWS })
      break
    }

    const pageIdx = sheet.page - 1 // 0-based
    if (pageIdx < 0 || pageIdx >= numPages) continue

    const newDoc = await PDFDocument.create()
    const [copiedPage] = await newDoc.copyPages(srcDoc, [pageIdx])
    newDoc.addPage(copiedPage)

    const pdfBytes = await newDoc.save()
    const blob = new Blob([pdfBytes], { type: "application/pdf" })
    const sheetNum = (sheet.sheet_number || "").replace(/[\s/\\]+/g, "-")
    const splitFileName = `${basename}_${sheetNum}.pdf`
    const splitFile = new File([blob], splitFileName, { type: "application/pdf" })

    count++
    yield {
      file: splitFile,
      sheetNumber: sheet.sheet_number || "",
      title: sheet.title || "",
      discipline: sheet.discipline || "",
      pageNumber: sheet.page
    }

    onProgress?.({ phase: "splitting", current: count, total: sheets.length })
    await new Promise(r => setTimeout(r, 0))
  }
}

// ── Drawing Page Splitter (programmatic fallback) ────────────────────────
// Async generator: yields null once if single-page PDF,
// or yields per-page entries progressively.

export async function* splitDrawingPages(file, arrayBuffer, onProgress) {
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer.slice(0) }).promise
  const numPages = pdf.numPages

  if (numPages <= 1) {
    yield null
    return
  }

  onProgress?.({ phase: "scanning", current: 0, total: numPages })

  // Extract text from all pages first (fast)
  const allPageTexts = []
  for (let i = 1; i <= numPages; i++) {
    const page = await pdf.getPage(i)
    const content = await page.getTextContent()
    const text = content.items.map(item => item.str).join(" ")
    allPageTexts.push(text)

    if (i % 20 === 0) {
      onProgress?.({ phase: "scanning", current: i, total: numPages })
      await new Promise(r => setTimeout(r, 0))
    }
  }

  onProgress?.({ phase: "scanned", totalPages: numPages })

  // Load with pdf-lib for splitting
  const srcDoc = await PDFDocument.load(arrayBuffer, { ignoreEncryption: true })
  const basename = file.name.replace(/\.pdf$/i, "")
  const sheetPattern = /^([A-Z]{1,2}[-.\s]?\d{2,4}[A-Za-z]?)/m

  let count = 0
  for (let i = 0; i < numPages; i++) {
    if (count >= MAX_SPLIT_ROWS) {
      onProgress?.({ phase: "maxReached", max: MAX_SPLIT_ROWS })
      break
    }

    const newDoc = await PDFDocument.create()
    const [copiedPage] = await newDoc.copyPages(srcDoc, [i])
    newDoc.addPage(copiedPage)

    const pdfBytes = await newDoc.save()
    const blob = new Blob([pdfBytes], { type: "application/pdf" })
    const pageNum = i + 1
    const splitFileName = `${basename}_Page_${String(pageNum).padStart(3, "0")}.pdf`
    const splitFile = new File([blob], splitFileName, { type: "application/pdf" })

    // Try to detect sheet number from text
    const pageText = allPageTexts[i] || ""
    const sheetMatch = pageText.match(sheetPattern)
    let sheetNumber = sheetMatch ? sheetMatch[1].trim() : ""
    let title = ""
    let discipline = ""

    if (sheetNumber) {
      // Normalize sheet number: ensure dash separator
      sheetNumber = sheetNumber.replace(/\s+/g, "-").replace(/\./, "-")

      // Detect discipline from prefix
      const prefixMatch = sheetNumber.match(/^([A-Z]{1,2})/)
      if (prefixMatch) {
        const prefix = prefixMatch[1]
        discipline = DISCIPLINE_PREFIXES[prefix] || ""
      }
    }

    count++
    yield {
      file: splitFile,
      pageNumber: pageNum,
      pageText,
      sheetNumber,
      title,
      discipline,
      _extracted: !!sheetNumber // mark if we got a sheet number via regex
    }

    // Micro-yield so browser paints
    await new Promise(r => setTimeout(r, 0))
  }
}
